import React, { useState, useEffect } from "react";
import {
  format,
  startOfWeek,
  addDays,
  isSameDay,
  addWeeks,
  subWeeks,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
} from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  User,
  Plus,
  Eye,
} from "lucide-react";
import { Link } from "react-router-dom";
import medicalDB from "../services/database";

const AppointmentCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("week"); // "week" or "month"
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayAppointments, setDayAppointments] = useState([]);

  useEffect(() => {
    loadAppointments();
  }, [currentDate, viewMode]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const allAppointments = await medicalDB.getAllAppointments();

      // Filter appointments based on current view
      const filteredAppointments = allAppointments.filter((appointment) => {
        const appointmentDate = new Date(appointment.date);

        if (viewMode === "week") {
          const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday start
          const weekEnd = addDays(weekStart, 6);
          return appointmentDate >= weekStart && appointmentDate <= weekEnd;
        } else {
          const monthStart = startOfMonth(currentDate);
          const monthEnd = endOfMonth(currentDate);
          return appointmentDate >= monthStart && appointmentDate <= monthEnd;
        }
      });

      setAppointments(filteredAppointments);
    } catch (error) {
      console.error("Error loading appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const navigatePrevious = () => {
    if (viewMode === "week") {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
      );
    }
  };

  const navigateNext = () => {
    if (viewMode === "week") {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
      );
    }
  };

  const navigateToday = () => {
    setCurrentDate(new Date());
  };

  const getWeekDays = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  };

  const getMonthDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = addDays(calendarStart, 41); // 6 weeks

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  };

  const getAppointmentsForDay = (date) => {
    return appointments.filter((appointment) =>
      isSameDay(new Date(appointment.date), date)
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getAppointmentTypeName = (type) => {
    const appointmentTypes = {
      ROUTINE: "Routine",
      FOLLOW_UP: "Follow-up",
      NEW_PATIENT: "New Patient",
      CONSULTATION: "Consultation",
      EMERGENCY: "Emergency",
    };
    return appointmentTypes[type] || type;
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
    setDayAppointments(getAppointmentsForDay(date));
  };

  const formatDateRange = () => {
    if (viewMode === "week") {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 6);

      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${format(weekStart, "MMM d")} - ${format(weekEnd, "d, yyyy")}`;
      } else {
        return `${format(weekStart, "MMM d")} - ${format(
          weekEnd,
          "MMM d, yyyy"
        )}`;
      }
    } else {
      return format(currentDate, "MMMM yyyy");
    }
  };

  const timeSlots = [
    "08:00",
    "08:30",
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
    "17:30",
    "18:00",
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading calendar...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Appointment Calendar
          </h1>
          <p className="text-gray-600">Manage your appointment schedule</p>
        </div>

        <Link
          to="/appointments/new"
          className="bg-medical-blue hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus size={20} />
          <span>New Appointment</span>
        </Link>
      </div>

      {/* Calendar Controls */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={navigatePrevious}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} />
            </button>

            <h2 className="text-xl font-semibold text-gray-900 min-w-[200px] text-center">
              {formatDateRange()}
            </h2>

            <button
              onClick={navigateNext}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={navigateToday}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Today
            </button>

            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("week")}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  viewMode === "week"
                    ? "bg-white text-medical-blue shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setViewMode("month")}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  viewMode === "month"
                    ? "bg-white text-medical-blue shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Month
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        {viewMode === "week" ? (
          <WeekView
            days={getWeekDays()}
            appointments={appointments}
            timeSlots={timeSlots}
            getAppointmentsForDay={getAppointmentsForDay}
            getStatusColor={getStatusColor}
            getAppointmentTypeName={getAppointmentTypeName}
            onDayClick={handleDayClick}
          />
        ) : (
          <MonthView
            days={getMonthDays()}
            currentDate={currentDate}
            appointments={appointments}
            getAppointmentsForDay={getAppointmentsForDay}
            getStatusColor={getStatusColor}
            getAppointmentTypeName={getAppointmentTypeName}
            onDayClick={handleDayClick}
          />
        )}
      </div>

      {/* Day Detail Modal */}
      {selectedDate && (
        <DayDetailModal
          date={selectedDate}
          appointments={dayAppointments}
          onClose={() => setSelectedDate(null)}
          getStatusColor={getStatusColor}
          getAppointmentTypeName={getAppointmentTypeName}
        />
      )}
    </div>
  );
};

// Week View Component
const WeekView = ({
  days,
  timeSlots,
  getAppointmentsForDay,
  getStatusColor,
  getAppointmentTypeName,
  onDayClick,
}) => {
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[800px]">
        {/* Day Headers */}
        <div className="grid grid-cols-8 gap-px bg-gray-200">
          <div className="bg-white p-3 text-sm font-medium text-gray-500">
            Time
          </div>
          {days.map((day) => (
            <div
              key={day.toISOString()}
              className="bg-white p-3 text-center cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => onDayClick(day)}
            >
              <div className="text-sm font-medium text-gray-900">
                {format(day, "EEE")}
              </div>
              <div
                className={`text-lg ${
                  isSameDay(day, new Date())
                    ? "text-medical-blue font-bold"
                    : "text-gray-700"
                }`}
              >
                {format(day, "d")}
              </div>
            </div>
          ))}
        </div>

        {/* Time Slots */}
        <div className="grid grid-cols-8 gap-px bg-gray-200">
          {timeSlots.map((time) => (
            <React.Fragment key={time}>
              <div className="bg-white p-2 text-xs text-gray-500 border-r">
                {time}
              </div>
              {days.map((day) => {
                const dayAppointments = getAppointmentsForDay(day);
                const timeAppointment = dayAppointments.find(
                  (apt) => apt.startTime === time
                );

                return (
                  <div
                    key={`${day.toISOString()}-${time}`}
                    className="bg-white p-1 min-h-[40px] border-r border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    {timeAppointment && (
                      <div
                        className={`text-xs p-1 rounded border ${getStatusColor(
                          timeAppointment.status
                        )}`}
                      >
                        <div className="font-medium truncate">
                          {timeAppointment.patient?.name}
                        </div>
                        <div className="truncate">
                          {getAppointmentTypeName(timeAppointment.type)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

// Month View Component
const MonthView = ({
  days,
  currentDate,
  getAppointmentsForDay,
  getStatusColor,
  getAppointmentTypeName,
  onDayClick,
}) => {
  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div>
      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-px bg-gray-200 mb-1">
        {weekDays.map((day) => (
          <div
            key={day}
            className="bg-white p-3 text-center text-sm font-medium text-gray-500"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-px bg-gray-200">
        {days.map((day) => {
          const dayAppointments = getAppointmentsForDay(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isToday = isSameDay(day, new Date());

          return (
            <div
              key={day.toISOString()}
              className={`bg-white p-2 min-h-[120px] cursor-pointer hover:bg-gray-50 transition-colors ${
                !isCurrentMonth ? "text-gray-400 bg-gray-50" : ""
              }`}
              onClick={() => onDayClick(day)}
            >
              <div
                className={`text-sm font-medium mb-1 ${
                  isToday
                    ? "text-medical-blue font-bold"
                    : isCurrentMonth
                    ? "text-gray-900"
                    : "text-gray-400"
                }`}
              >
                {format(day, "d")}
              </div>

              <div className="space-y-1">
                {dayAppointments.slice(0, 3).map((appointment) => (
                  <div
                    key={appointment.id}
                    className={`text-xs p-1 rounded border ${getStatusColor(
                      appointment.status
                    )}`}
                  >
                    <div className="font-medium truncate">
                      {appointment.startTime}
                    </div>
                    <div className="truncate">{appointment.patient?.name}</div>
                  </div>
                ))}

                {dayAppointments.length > 3 && (
                  <div className="text-xs text-gray-500 text-center">
                    +{dayAppointments.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Day Detail Modal Component
const DayDetailModal = ({
  date,
  appointments,
  onClose,
  getStatusColor,
  getAppointmentTypeName,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {format(date, "EEEE, MMMM d, yyyy")}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {appointments.length > 0 ? (
            <div className="space-y-3">
              {appointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="border border-gray-200 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Clock size={16} className="text-gray-500" />
                      <span className="font-medium">
                        {appointment.startTime} - {appointment.endTime}
                      </span>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${getStatusColor(
                        appointment.status
                      )}`}
                    >
                      {appointment.status}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 mb-2">
                    <User size={16} className="text-gray-500" />
                    <span className="font-medium">
                      {appointment.patient?.name}
                    </span>
                  </div>

                  <div className="text-sm text-gray-600">
                    {getAppointmentTypeName(appointment.type)}
                    {appointment.notes && ` • ${appointment.notes}`}
                  </div>

                  <div className="mt-2 flex space-x-2">
                    <Link
                      to={`/patients/${appointment.patientId}`}
                      className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors flex items-center space-x-1"
                    >
                      <Eye size={12} />
                      <span>View Patient</span>
                    </Link>
                    <Link
                      to={`/appointments/${appointment.id}/edit`}
                      className="text-xs bg-medical-blue hover:bg-blue-700 text-white px-2 py-1 rounded transition-colors"
                    >
                      Edit
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600">
                No appointments scheduled for this day
              </p>
              <Link
                to={`/appointments/new?date=${format(date, "yyyy-MM-dd")}`}
                className="mt-3 inline-flex items-center space-x-2 text-medical-blue hover:text-blue-700 transition-colors"
              >
                <Plus size={16} />
                <span>Schedule Appointment</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentCalendar;
